import axios from 'axios';
import ApiResponseError from '../../infrastructure/Exceptions/ApiResponseError';
import { ErrorTypes } from '../../infrastructure/Exceptions/utils/ResponseErrorUtils';
import {
  getOtpSignInFailureReason,
  getRaFSignUpEventObject,
  getSignInAttemptEventObjectVoyagers,
  getSignInEventObject,
  getSignInFailureEventObjectVoyagers,
  getSignInSuccessEventObjectVoyagers,
  getSignUpAttemptEventObjectVoyagers,
  getSignUpEventObject,
  getSignUpFailureEventObjectVoyagers,
  getSignUpSuccessEventObjectVoyagers
} from '../../infrastructure/middleware/analytics/analyticsObjectBuilders';
import { GTM_EVENT } from '../../utilities/constants';
import { emitEventsByName, EVENTS_DICTIONARY } from '../../utilities/customEventsHelpers';
import { pushEvent } from '../../utilities/dataLayerUtils';
import { getSpecificItem, SessionStorageKeys } from '../../utilities/storageUtils';
import { API_URLS, getApiUrl } from '../Utils/apiURLConfiguration';

import { isPaymentPage } from '../../utilities/planUtils';
import { successfulAuthCallback } from '../Utils/authUtils';

const headersConfig = {
  'Content-Type': 'application/json',
  'x-client-type': 'web'
};

class AuthClient {
  constructor() {
    this.tioga_url = getApiUrl(API_URLS.REACT_APP_USERACCOUNT_ENDPOINT_TIOGA, 'v1');
    this.currentRenew = null;
  }

  /**
   * @function
   * Gets the currently logged in user if the user is logged in
   */
  getUser() {
    const user = localStorage.getItem('user');
    if (!user) return null;
    else return JSON.parse(user);
  }

  /**
   * @function
   * Attempts a login request with the given credentials
   * @param {string} username
   * Email
   * @param {string} password
   * Password
   */
  async login(username, password, url, calledFromVoyagers, eventLocation) {
    const urlRequest = `${this.tioga_url}/login`;
    const signInLocationPaymentPage = 'payment page';
    const eventLocationName = eventLocation ? eventLocation : isPaymentPage() ? signInLocationPaymentPage : '';
    if (calledFromVoyagers) pushEvent(getSignInAttemptEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
    else pushEvent(getSignInEventObject(GTM_EVENT.NAME.signInAttempt, GTM_EVENT.LABEL.greatWolf, eventLocationName));

    return await axios
      .post(
        urlRequest,
        {
          username: username,
          password: password,
          url: url
        },
        {
          // This header must be like this, to be able to perfom a string POST
          headers: headersConfig,
          withCredentials: true
        }
      )
      .then(response => {
        localStorage.removeItem('user');
        if (calledFromVoyagers) pushEvent(getSignInSuccessEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
        else
          pushEvent(getSignInEventObject(GTM_EVENT.NAME.signInSuccess, GTM_EVENT.LABEL.greatWolf, eventLocationName));
        successfulAuthCallback(response);
        return response.data;
      })
      .catch(err => {
        if (calledFromVoyagers) pushEvent(getSignInFailureEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
        else
          pushEvent(getSignInEventObject(GTM_EVENT.NAME.signInFailure, GTM_EVENT.LABEL.greatWolf, eventLocationName));
        throw new ApiResponseError(ErrorTypes.ResponseError, err);
      });
  }

  /**
   * @function
   * Requests a one-time passcode to be sent to the given email
   * @param {string} email
   * Email
   */
  async requestOtp(email) {
    const urlRequest = `${this.tioga_url}/otp/send`;

    try {
      const response = await axios.post(
        urlRequest,
        // The endpoint expects the email as a raw JSON string body, not an object
        JSON.stringify(email),
        {
          headers: headersConfig,
          withCredentials: true
        }
      );
      return response.data;
    } catch (err) {
      throw new ApiResponseError(ErrorTypes.ResponseError, err);
    }
  }

  /**
   * @function
   * Attempts a login request with the given email and one-time passcode
   * @param {string} email
   * Email
   * @param {string} code
   * One-time passcode sent to the email
   * @param calledFromVoyagers
   * @param eventLocation
   */
  async verifyOtp(email, code, calledFromVoyagers, eventLocation) {
    const urlRequest = `${this.tioga_url}/otp/check`;
    // GTM Event data for location
    const signInLocationPaymentPage = 'payment page';
    const eventLocationName = eventLocation ? eventLocation : isPaymentPage() ? signInLocationPaymentPage : '';
    // GTM Event data for sign-in failure
    const pushSignInFailure = reason => {
      const failureEvent = calledFromVoyagers
        ? getSignInFailureEventObjectVoyagers(GTM_EVENT.LABEL.otp)
        : getSignInEventObject(GTM_EVENT.NAME.signInFailure, GTM_EVENT.LABEL.otp, eventLocationName);
      pushEvent({ ...failureEvent, sign_in_failure_reason: reason });
    };

    try {
      const response = await axios.post(
        urlRequest,
        { email, code },
        {
          headers: headersConfig,
          withCredentials: true
        }
      );
      // A 2xx can still reject the code through a boolean isValid; a missing or null flag means a valid login
      if (typeof response.data?.isValid === 'boolean' && !response.data.isValid) {
        pushSignInFailure(getOtpSignInFailureReason({ errorCode: response.data.errorCode, isValid: false }));
        return response;
      }
      // clear local storage
      localStorage.removeItem('user');
      // GTM Event data for sign-in
      if (calledFromVoyagers) pushEvent(getSignInSuccessEventObjectVoyagers(GTM_EVENT.LABEL.otp));
      else pushEvent(getSignInEventObject(GTM_EVENT.NAME.signInSuccess, GTM_EVENT.LABEL.otp, eventLocationName));
      successfulAuthCallback(response);
      // Returns the whole response because it is called through useApiClient, which unwraps .data
      return response;
    } catch (err) {
      pushSignInFailure(
        getOtpSignInFailureReason({ status: err?.response?.status, errorCode: err?.response?.data?.errorCode })
      );
      throw err;
    }
  }

  /**
   * @function
   * Refreshes the access token
   */
  async renewToken(addApiLog = false) {
    const urlRequest = `${this.tioga_url}/renew-token`;
    const user = this.getUser();
    if (!user || !user.id) return;

    if (!this.currentRenew) {
      this.currentRenew = axios.post(
        urlRequest,
        {
          userId: user.id,
          refreshToken: user.refreshToken
        },
        {
          // This header must be like this, to be able to perfom a string POST
          headers: headersConfig,
          withCredentials: true
        }
      );

      this.currentRenew.then(
        response => {
          this.currentRenew = null;
          successfulAuthCallback(response);
        },
        err => {
          if (addApiLog) {
            // Lazy import to avoid a circular dependency
            // (AuthClient -> applicationInsights -> offer.selectors -> offer.reducer -> AuthClient)
            import('../../utilities/applicationInsights').then(({ EVENT_CATEGORIES, generalAppInsightsTrack }) => {
              generalAppInsightsTrack(`API error on renew`, EVENT_CATEGORIES.apiGeneralError);
            });
          }
          this.currentRenew = null;
          this.logout();
          throw new ApiResponseError(ErrorTypes.ResponseError, err);
        }
      );
    }

    return this.currentRenew;
  }

  /**
   * @function
   * Logs the user out the website
   */
  async logout() {
    localStorage.removeItem('user');
    const urlRequest = `${this.tioga_url}/logout`;

    return await axios
      .post(
        urlRequest,
        {},
        {
          // This header must be like this, to be able to perfom a string POST
          headers: headersConfig,
          withCredentials: true
        }
      )
      .catch(err => {
        throw new ApiResponseError(ErrorTypes.ResponseError, err);
      });
  }

  /**
   * @function
   * Authenticates with the GW backend to retrieve an internal access token and user data
   * @param {string} provider
   * The external auth provider (Facebook or Google)
   * @param {string} accessToken
   * The external auth provider's access token for the user
   * @param {string} id
   */
  async thirdPartyLogin(
    provider,
    accessToken,
    id,
    postLoginCallback = null,
    appleUser = null,
    isSignIn = false,
    locationCode = ''
  ) {
    const urlRequest = `${this.tioga_url}/provider-signin`;

    let requestObject = {
      provider,
      token: accessToken,
      key: id,
      appleUser,
      isSignIn,
      property: locationCode
    };
    if (sessionStorage.getItem(SessionStorageKeys.REFER_A_FRIEND)) {
      requestObject.LoyaltyReferralCode = getSpecificItem('ReferralCode', SessionStorageKeys.REFER_A_FRIEND);
    }

    try {
      const response = await axios.post(urlRequest, requestObject, {
        // This header must be like this, to be able to perfom a string POST
        headers: headersConfig,
        withCredentials: true
      });
      localStorage.removeItem('user');
      successfulAuthCallback(response, postLoginCallback);
      emitEventsByName(EVENTS_DICTIONARY.GWR_LOGIN);
      return response.data;
    } catch (err) {
      throw new ApiResponseError(ErrorTypes.ResponseError, err);
    }
  }

  /**
   * @function
   * Creates a new user account and logs him/her in
   * @param {object} newUser
   * The new user to create
   */
  async createAccount(newUser, isLeadGenSignUp = false, calledFromVoyagers, eventLocation) {
    const urlRequest = `${getApiUrl(API_URLS.REACT_APP_USERACCOUNT_ENDPOINT_TIOGA, 'v1')}/User`;

    const contactByPhone = true; // This is true by default on new users
    if (calledFromVoyagers) pushEvent(getSignUpAttemptEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
    else
      pushEvent(
        getSignUpEventObject(GTM_EVENT.NAME.signUpAttempt, GTM_EVENT.LABEL.greatWolf, isLeadGenSignUp, eventLocation)
      );

    // Refer a Friend 25/50 test - GA Event
    const rafEventData = getRaFSignUpEventObject();
    pushEvent(rafEventData);

    return await axios
      .post(
        urlRequest,
        {
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          zip: newUser.zip,
          password: newUser.password,
          property: newUser.property,
          caslOptIn: newUser.caslOptIn || '',
          marketingEmailOptIn: newUser.marketingEmailOptions || '',
          canContactByPhone: contactByPhone,
          canContactByEmail: !!newUser.canContactByEmail,
          canContactByMail: !!newUser.canContactByMail,
          canContactBySms: !!newUser.contactBySms,
          loyaltyReferralCode: newUser.ReferralCode || '',
          refereeId: newUser.MemberId || ''
        },
        {
          headers: headersConfig,
          withCredentials: true
        }
      )
      .then(
        response => {
          localStorage.removeItem('user');
          if (calledFromVoyagers) {
            pushEvent(getSignUpSuccessEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
          } else {
            pushEvent(
              getSignUpEventObject(
                GTM_EVENT.NAME.signUpSuccess,
                GTM_EVENT.LABEL.greatWolf,
                isLeadGenSignUp,
                eventLocation
              )
            );
          }
          successfulAuthCallback(response);
          emitEventsByName(EVENTS_DICTIONARY.GWR_LOGIN);
          return response.data;
        },
        err => {
          if (calledFromVoyagers) {
            pushEvent(getSignUpFailureEventObjectVoyagers(GTM_EVENT.LABEL.greatWolf));
          } else {
            pushEvent(
              getSignUpEventObject(
                GTM_EVENT.NAME.signUpFailure,
                GTM_EVENT.LABEL.greatWolf,
                isLeadGenSignUp,
                eventLocation
              )
            );
          }

          throw new ApiResponseError(ErrorTypes.ResponseError, err);
        }
      );
  }

  /**
   * @function
   * Creates a new LeadGen user account and logs him/her in
   * @param {object} leadGenUser
   * The new user to create
   */
  async createLeadGenAccount(leadGenUser, isAPI_2_2_Active = false) {
    let urlRequest = `${getApiUrl(API_URLS.REACT_APP_USER_SERVICE_ENDPOINT, 'v2.1')}/User`;
    if (isAPI_2_2_Active) {
      urlRequest = `${getApiUrl(API_URLS.REACT_APP_USER_SERVICE_ENDPOINT, 'v2.2')}/User`;
    }
    const actionUrl = '/profile';

    const eventData = getSignUpEventObject(GTM_EVENT.NAME.signUpAttempt, GTM_EVENT.LABEL.greatWolf);
    pushEvent(eventData);

    // Refer a Friend 25/50 test - GA Event
    const rafEventData = getRaFSignUpEventObject();
    pushEvent(rafEventData);

    return await axios
      .post(
        `${urlRequest}${actionUrl}`,
        {
          profileKey: leadGenUser.profileKey,
          password: leadGenUser.password,
          property: leadGenUser.property,
          profileId: leadGenUser.profileId,
          marketingEmailOptIn: leadGenUser.marketingEmailOptions || '',
          loyaltyReferralCode: leadGenUser.ReferralCode || '',
          refereeId: leadGenUser.MemberId || ''
        },
        {
          headers: headersConfig,
          withCredentials: true
        }
      )
      .then(
        response => {
          localStorage.removeItem('user');
          const eventData = getSignUpEventObject(GTM_EVENT.NAME.signUpSuccess, GTM_EVENT.LABEL.greatWolf);
          pushEvent(eventData);
          successfulAuthCallback(response);
          emitEventsByName(EVENTS_DICTIONARY.GWR_LOGIN);
          return response.data;
        },
        err => {
          pushEvent(getSignUpEventObject(GTM_EVENT.NAME.signUpFailure, GTM_EVENT.LABEL.greatWolf));

          throw new ApiResponseError(ErrorTypes.ResponseError, err);
        }
      );
  }

  /**
   * @function
   * Check if the current user is logged in
   * @returns {boolean}
   */
  isLoggedIn() {
    var user = this.getUser();

    return user !== undefined && user !== null && user.id !== null;
  }

  /**
   * @function
   * Check if the current user is partial logged in
   * @returns {boolean}
   */
  isReservationOnlyLogin() {
    var user = this.getUser();
    return user !== undefined && user !== null && user.isReservationOnlyToken;
  }

  /**
   * @function
   * Get user ID if logged in
   * @returns {boolean}
   */
  getUserId() {
    var user = this.getUser();

    if (user !== undefined && user !== null && user.id !== null) {
      return user.id;
    } else {
      return null;
    }
  }

  /**
   * @function
   * Check if there is an auth token present
   *   Note: JS is forced to use presence of 'user.accessTokenExpiry' field as a telltale sign that an auth token is
   *         present, since actual token is stored in a secured cookie and we can't access it via JS)
   * @returns {boolean}
   */
  isTokenPresent() {
    var user = this.getUser();

    return user !== undefined && user !== null && user.accessTokenExpiry !== null;
  }
}

export default AuthClient;
